import { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';

export default function ShoppingList({ ingredientList, weekStart }) {
  // Initialize checked state for all items
  const [checked, setChecked] = useState({});
  const [categories, setCategories] = useState(ingredientList);

  // State for new custom item input
  const [newItemName, setNewItemName] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState('');

  // Sync local checked and categories when prop changes
  useEffect(() => {
    const initChecked = {};
    ingredientList.forEach((category) =>
      category.categoryIngredients.forEach((_, idx) => {
        initChecked[`${category.categoryLabel}-${idx}`] = false;
      })
    );
    setChecked(initChecked);
    setCategories(ingredientList);
  }, [ingredientList]);

  const toggle = (key) => {
    setChecked((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleAddItem = async () => {
    if (!newItemName.trim()) return;
    try {
      const res = await apiClient.post(
        `/shopping-list/${weekStart}/custom`,
        { ingredientName: newItemName, ingredientQuantity: newItemQuantity }
      );
      setCategories(res.data.categories);
      setNewItemName('');
      setNewItemQuantity('');
    } catch (err) {
      console.error('Error adding custom item', err);
    }
  };

  const handleDeleteItem = async (name) => {
    try {
      const res = await apiClient.delete(
        `/shopping-list/${weekStart}/custom`,
        { data: { ingredientName: name } }
      );
      setCategories(res.data.categories);
    } catch (err) {
      console.error('Error deleting custom item', err);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 mt-10 space-y-6">
      {categories.map((category) => (
        <div key={category.categoryLabel} className="space-y-2">
          <h2 className="text-xl font-semibold border-b pb-1">
            {category.categoryLabel}
          </h2>
          <ul className="pl-4 space-y-1">
            {category.categoryIngredients.map((item, idx) => {
              const key = `${category.categoryLabel}-${idx}`;
              return (
                <li key={key} className="flex items-center justify-between">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={checked[key] || false}
                      onChange={() => toggle(key)}
                      className="form-checkbox h-5 w-5 text-green-600"
                    />
                    <span
                      className={
                        checked[key]
                          ? 'line-through text-gray-400'
                          : 'text-gray-900'
                      }
                    >
                      {item.ingredientQuantity && (
                        <>{item.ingredientQuantity} — </>
                      )}
                      {item.ingredientName}
                    </span>
                  </label>
                  {/* Delete button for custom items only */}
                  {item.isCustom && (
                    <button
                      onClick={() => handleDeleteItem(item.ingredientName)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Delete
                    </button>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Render Add Item UI only for Custom/My Items category */}
          {['My Items', 'Custom'].includes(category.categoryLabel) && (
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:gap-2">
              <input
                type="text"
                placeholder="Item name"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                className="border p-1 rounded flex-1"
              />
              <input
                type="text"
                placeholder="Quantity (optional)"
                value={newItemQuantity}
                onChange={(e) => setNewItemQuantity(e.target.value)}
                className="border p-1 rounded flex-1 mt-2 sm:mt-0"
              />
              <button
                onClick={handleAddItem}
                className="mt-2 sm:mt-0 bg-sage text-white px-3 py-1 rounded hover:bg-sagedark"
              >
                Add Item
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
